/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { SearchPageNode } from '@atlassian/search-client';

import { ApplicationModes } from '../../constants/application-modes';
import { type PrimaryProductKey } from '../../constants/products';
import SearchResultContextMenu from '../context-menu';
import { SearchMetadata } from '../search-metadata';
import { SearchResultTitle } from '../search-result-title';

const searchResultContainerStyles = xcss({
	position: 'relative',
});

const iconAreaStyles = xcss({
	gridArea: 'icon',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	width: token('space.600'),
});

const newIconStyles = xcss({
	marginBlockStart: 'space.075',
});

const iconStyles = xcss({
	padding: 'space.050',
});

const headerRightAreaStylesInVisible = xcss({
	gridArea: 'header-right',
	display: 'flex',
	opacity: 0,
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});

const headerRightAreaStylesVisible = xcss({
	gridArea: 'header-right',
	display: 'flex',
	opacity: 1,
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});

const bodyAreaStyles = xcss({
	gridArea: 'body',
	paddingTop: 'space.050',
	overflowWrap: 'break-word',
});

const footerAreaStyles = xcss({
	gridArea: 'footer',
});

type SearchResultProps = {
	id?: string;
	product?: string;
	productSelected?: boolean;
	primaryProduct?: PrimaryProductKey;
	description: ReactNode;
	footer?: ReactNode;
	icon: ReactNode;
	headerMetadataParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	headerMetadataSocialParts?: any[]; // TODO: CHANGE THIS TO A MORE SPECIFIC TYPE
	titleMetadata?: ReactNode;
	title: ReactNode;
	url?: string;
	isThirdParty?: boolean;
	showAISummary?: boolean;
	position?: number;
	partial?: SearchPageNode;
	cloudId?: string;
	appMode?: ApplicationModes;
	/** Called when the result is clicked */
	onResultClicked?: VoidFunction;

	/** Called when the result context menu is opened */
	onResultContextMenu?: VoidFunction;

	/** Called when the copy link button is clicked */
	onCopyLinkButtonClicked?: VoidFunction;
	isLoadingSkeleton?: boolean;
};

/**
 * This component is intended to be as generic as possible.
 * It should provide generic "slots" for users of this component to add
 * their content in.
 */
export const SearchResult = ({
	id,
	product,
	productSelected,
	primaryProduct,
	description,
	footer,
	icon,
	url,
	showAISummary = false,
	cloudId, // required for AI Summaries
	title,
	isThirdParty,
	appMode,
	headerMetadataParts,
	headerMetadataSocialParts,
	titleMetadata,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	isLoadingSkeleton = false,
}: SearchResultProps) => {
	const {
		isContextMenuOpen,
		isContextMenuVisible,
		setIsContextMenuOpen,
		setIsHovered,
		setIsFocused,
		isHovered,
	} = useVisibilityState();

	const containerRef = useRef<HTMLDivElement | null>(null);

	const aiSummaryEnabled = Boolean(showAISummary && id && cloudId && primaryProduct);

	const useNewIconStyles = fg('platform-team25-app-icon-tiles');

	return (
		<Box
			role="listitem"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocusCapture={() => setIsFocused(true)}
			onBlurCapture={() => setIsFocused(false)}
			testId={isLoadingSkeleton ? 'search-page-result-skeleton' : 'search-page-result'}
			ref={containerRef}
			xcss={searchResultContainerStyles}
		>
			<Grid
				columnGap="space.150"
				rowGap="space.050"
				templateAreas={[
					'icon heading header-right',
					'icon subheading-left header-right',
					'icon body body',
					'icon footer footer',
				]}
				templateColumns="auto 1fr auto"
			>
				<Box xcss={iconAreaStyles}>
					<Box xcss={useNewIconStyles ? newIconStyles : iconStyles}>{icon}</Box>
				</Box>
				<SearchResultTitle
					id={id}
					title={title}
					url={url}
					isThirdParty={isThirdParty}
					appMode={appMode}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					isHovered={isHovered}
					titleMetadata={titleMetadata}
				/>
				<SearchMetadata
					searchResultId={id}
					product={product}
					productSelected={productSelected}
					parts={headerMetadataParts}
					socialParts={headerMetadataSocialParts}
					containerRef={containerRef}
				/>
				{!isLoadingSkeleton && ( // Context Menu should not be rendered when rendering as a loading skeleton
					<Box
						xcss={
							isContextMenuVisible ? headerRightAreaStylesVisible : headerRightAreaStylesInVisible
						}
						testId="search-result-context-menu"
					>
						<SearchResultContextMenu
							url={url}
							isContextMenuOpen={isContextMenuOpen}
							setIsContextMenuOpen={setIsContextMenuOpen}
							setIsFocused={setIsFocused}
							setIsHovered={setIsHovered}
							id={id || ''}
							cloudId={cloudId}
							aiSummaryEnabled={aiSummaryEnabled}
						/>
					</Box>
				)}
				<Box xcss={bodyAreaStyles}>{description}</Box>
				<Box xcss={footerAreaStyles}>{footer}</Box>
			</Grid>
		</Box>
	);
};

const useVisibilityState = () => {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

	return {
		isContextMenuVisible: isHovered || isFocused || isContextMenuOpen,
		isContextMenuOpen,
		isHovered,
		setIsHovered,
		setIsFocused,
		setIsContextMenuOpen,
	};
};
